import { useState, useEffect } from "react"
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Eye, EyeOff, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react"
import { auth } from "@/lib/firebase"
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth"

type ResetStep = 'loading' | 'input' | 'success' | 'error' | 'expired'

export default function PasswordResetPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const [step, setStep] = useState<ResetStep>('loading')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Get the oobCode (action code) from URL
  const oobCode = searchParams.get('oobCode')
  
  // Verify the reset code on mount
  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setStep('error')
        setError('Invalid password reset link. Please request a new one.')
        return
      }
      
      try {
        // Verify the code and get the email
        const userEmail = await verifyPasswordResetCode(auth, oobCode)
        setEmail(userEmail)
        setStep('input')
      } catch (err: any) {
        console.error('Error verifying reset code:', err)
        if (err.code === 'auth/expired-action-code') {
          setStep('expired')
        } else if (err.code === 'auth/invalid-action-code') {
          setStep('expired')
        } else {
          setStep('error')
          setError('Invalid password reset link. Please request a new one.')
        }
      }
    }
    
    void verifyCode()
  }, [oobCode])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    // Validate password length
    if (password.length < 6) {
      setError(t('auth.weakPassword'))
      return
    }
    
    // Check passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    if (!oobCode) {
      setError('Invalid reset code')
      return
    }
    
    setLoading(true)
    
    try {
      await confirmPasswordReset(auth, oobCode, password)
      setStep('success')
    } catch (err: any) {
      console.error('Error resetting password:', err)
      if (err.code === 'auth/expired-action-code') {
        setStep('expired')
      } else if (err.code === 'auth/weak-password') {
        setError(t('auth.weakPassword'))
      } else {
        setError(t('auth.genericError'))
      }
    } finally {
      setLoading(false)
    }
  }
  
  const handleGoToLogin = () => {
    navigate('/feed')
  }
  
  const handleRequestNewLink = () => {
    navigate('/feed')
  }
  
  const handleBackClick = () => {
    // Navigate back in history, or to feed if no history
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/feed')
    }
  }
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)] bg-background rounded-[var(--card-radius)] shadow-lg border border-border overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackClick}
              className="p-2 -ml-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="sr-only">Go back</span>
            </Button>
            <h2 className="text-xl font-semibold text-foreground">
              {step === 'success' ? 'Password Reset! 🎉' : 
               step === 'expired' ? 'Link Expired 😅' :
               step === 'error' ? 'Oops! 🤔' :
               'Reset Your Password 🔐'}
            </h2>
          </div>
        </div>
        
        {/* Content */}
        <div className="px-6 pb-6">
          {/* Loading State */}
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mb-4"></div>
              <p className="text-muted-foreground">Verifying your reset link...</p>
            </div>
          )}
          
          {/* Input Form */}
          {step === 'input' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Display */}
              <div className="text-sm text-muted-foreground leading-relaxed">
                Enter a new password for <span className="font-medium text-foreground">{email}</span>
              </div>
              
              {/* New Password Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  New Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      if (error) setError(null)
                    }}
                    placeholder="Enter your new password"
                    required
                    disabled={loading}
                    minLength={6}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Confirm Password Field */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Confirm Password
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value)
                      if (error) setError(null)
                    }}
                    placeholder="Confirm your new password"
                    required
                    disabled={loading}
                    minLength={6}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Password Requirements */}
              <p className="text-xs text-muted-foreground">
                Password must be at least 6 characters long
              </p>
              
              {/* Error Message */}
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[var(--card-radius)]">
                  {error}
                </div>
              )}
              
              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full px-6 py-6 text-base font-medium rounded-[var(--card-radius)]"
                size="lg"
                disabled={loading}
              >
                {loading ? t('common.loading') : 'Reset Password'}
              </Button>
            </form>
          )}
          
          {/* Success State */}
          {step === 'success' && (
            <div className="space-y-4 text-center py-4">
              <div className="flex justify-center">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <div className="space-y-2">
                <p className="text-base text-foreground leading-relaxed">
                  Your password has been reset successfully!
                </p>
                <p className="text-sm text-muted-foreground">
                  You can now log in with your new password.
                </p>
              </div>
              
              <Button
                type="button"
                className="w-full px-6 py-6 text-base font-medium rounded-[var(--card-radius)]"
                size="lg"
                onClick={handleGoToLogin}
              >
                Go to Login
              </Button>
            </div>
          )}
          
          {/* Expired Link State */}
          {step === 'expired' && (
            <div className="space-y-4 text-center py-4">
              <div className="flex justify-center">
                <AlertCircle className="h-16 w-16 text-amber-500" />
              </div>
              <div className="space-y-2">
                <p className="text-base text-foreground leading-relaxed">
                  This password reset link has expired or has already been used.
                </p>
                <p className="text-sm text-muted-foreground">
                  Please request a new password reset link.
                </p>
              </div>
              
              <Button
                type="button"
                className="w-full px-6 py-6 text-base font-medium rounded-[var(--card-radius)]"
                size="lg"
                onClick={handleRequestNewLink}
              >
                Request New Link
              </Button>
            </div>
          )}
          
          {/* Error State */}
          {step === 'error' && (
            <div className="space-y-4 text-center py-4">
              <div className="flex justify-center">
                <AlertCircle className="h-16 w-16 text-destructive" />
              </div>
              <div className="space-y-2">
                <p className="text-base text-foreground leading-relaxed">
                  {error || 'Something went wrong with your password reset link.'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Please try requesting a new password reset.
                </p>
              </div>
              
              <Button
                type="button"
                className="w-full px-6 py-6 text-base font-medium rounded-[var(--card-radius)]"
                size="lg"
                onClick={handleRequestNewLink}
              >
                Request New Link
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

